import React, { useState, useEffect, useMemo } from "react";
import { Badge, Box, Divider, Grid2, Popover, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import CustomIcon from "../../../../Components/CustomIcon";
import CustomTag from "../../../../Components/CustomTag";
import { getMetricTimeStamp } from "../../../../Helper/DatePicker/DateConverter";
import { PrimaryTooltip } from "../../../../Components/StyledComponent";
import Lottie from "react-lottie";
import GreenBlinkingLottie from "../../../../Components/Lotties/GreenBlinkingLottie.json";
import { normalizeKey } from "../../../../Helper/Common/common_helpers";
import { formatSocValue } from "../../../../Helper/DigitalTwin/DigitalTwinHelper";
import TextWithCopy from "../../../../Features/Table/GenericDraggableTable/Renderers/columnTypes/TextWithCopy";
import CloseButton from "../../../../Components/CloseButton";
import DotSeparator from "../../../../Components/DotSeparator";
import { useBatteryInfo, useVehicleInfo } from "../../../../Pages/DigitalTwinV3/DigitalTwinContext";
import { getIoTState } from "../../../../Api/IoTApi";
import EditVehicleDetailsDialog from "../../../../Components/EditVehicleDetailsDialog";
import BatteryDeviceInfoMapDialog from "./BatteryDeviceInfoMapDialog";
import { headerNeedsIotState, subHeaderLayout, resolveTitle, signalStrengthBars, valueFromSource } from "./headerConfig";
import { usePlatformAlertsAccess } from "../../../../Hooks/usePlatformAlertsAccess";
import FieldStatusIndicator from "../../../../Components/FieldStatusIndicator";


const popoverStyles = {
    width: "fit-content",
    borderRadius: "10px",
    backgroundColor: "#FFFFFF",
    boxShadow: "-2px -2px 4px 0px rgba(151,151,151,0.25), 2px 2px 4px 0px rgba(151,151,151,0.25)",
};

function AboutPopover({ config, batteryInfo, sources, primaryValue }) {
    const { t } = useTranslation(["common", "digital_twin"]);
    const [anchorEl, setAnchorEl] = useState(null);
    const [openViaClick, setOpenViaClick] = useState(false);
    const open = Boolean(anchorEl);

    const primaryDetails = config?.title?.primaryDetails || {};

    const handleHoverOn = (e) => setAnchorEl(e.currentTarget);
    const handleHoverOff = () => { if (!openViaClick) setAnchorEl(null); };
    const handleClick = (e) => { setOpenViaClick(true); setAnchorEl(e.currentTarget); };
    const handleClose = () => { setOpenViaClick(false); setAnchorEl(null); };

    const getSourceValue = (field) => valueFromSource(sources, field);

    return (
        <>
            <Typography
                onMouseEnter={handleHoverOn}
                onMouseLeave={handleHoverOff}
                onClick={handleClick}
                sx={{
                    cursor: primaryDetails.clickable === false ? "default" : "pointer",
                    lineHeight: "16px",
                    textDecoration: openViaClick && primaryDetails.clickable !== false ? "underline" : "",
                    "&:hover": primaryDetails.clickable !== false ? { textDecoration: "underline" } : {},
                }}
                variant="fw600s16lh24c444B51"
            >
                {primaryValue ?? "-"}
            </Typography>
            {config?.aboutPopup?.enabled && (
                <Popover
                    sx={{ pointerEvents: openViaClick ? "" : "none" }}
                    open={open}
                    anchorEl={anchorEl}
                    anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                    transformOrigin={{ vertical: "top", horizontal: "left" }}
                    slotProps={{ paper: { sx: popoverStyles } }}
                    onClose={handleClose}
                    disableRestoreFocus
                >
                    <Box sx={{ minWidth: "350px", p: 3 }}>
                        <Grid2 container justifyContent="space-between" alignItems="center">
                            <Typography variant="fw600s16c000000" sx={{ lineHeight: "24px" }}>
                                {t("common:about")}
                            </Typography>
                            {openViaClick && <CloseButton onClick={handleClose} />}
                        </Grid2>
                        <Grid2 sx={{ mt: 1.5, mb: 1.5, display: "flex", alignItems: "center", gap: 1 }}>
                            <Box sx={{ display: "flex", alignItems: "center" }}>
                                <Badge
                                    variant="dot"
                                    sx={{
                                        mr: 0.5,
                                        "& .MuiBadge-badge": {
                                            position: "relative",
                                            transform: "none",
                                            backgroundColor: batteryInfo.pingStatus?.toLowerCase() === "active" ? "#09B820" : "#8F8F8F",
                                            width: "10px", height: "10px", minWidth: "10px", borderRadius: "50%",
                                        }
                                    }}
                                />
                                <Typography variant="fw400s12c000000" sx={{ lineHeight: "24px" }}>
                                    {batteryInfo.pingStatus?.toLowerCase() === "active" ? t("common:online") : t("common:offline")}
                                </Typography>
                            </Box>
                            <DotSeparator />
                            <Box>
                                <Typography variant="fw400s12c000000" sx={{ lineHeight: "24px" }}>
                                    {t("digital_twin:last_pinged")}&nbsp;:&nbsp;
                                </Typography>
                                {batteryInfo.lastPingedTime ? (
                                    <PrimaryTooltip title={getMetricTimeStamp(batteryInfo.lastPingedTime)} placement="bottom">
                                        <Typography variant="fw500s12lh24c444B51">
                                            {getMetricTimeStamp(batteryInfo.lastPingedTime, "relative")}
                                        </Typography>
                                    </PrimaryTooltip>
                                ) : (
                                    <Typography variant="fw500s12lh24c444B51">NA</Typography>
                                )}
                            </Box>
                        </Grid2>
                        <Divider sx={{ borderWidth: "0.5px", borderColor: "#E9E9E9" }} />
                        <Grid2 container gap={1} sx={{ mt: 1.5, pt: 1, pb: 1, flexDirection: "column" }}>
                            {(config.aboutPopup.fields || []).map((field) => {
                                const val = getSourceValue(field);
                                return (
                                    <Box key={field.key} sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}>
                                        <Typography variant="fw400s14c8F8F8F" sx={{ lineHeight: "24px" }}>
                                            {field.label}:
                                        </Typography>
                                        {val && field.copyable ? (
                                            <TextWithCopy text={val} type={field.label} textVariant="fw400s14c000000" />
                                        ) : (
                                            <Typography variant="fw400s14c000000" sx={{ lineHeight: "24px" }}>
                                                {val || "-"}
                                            </Typography>
                                        )}
                                    </Box>
                                );
                            })}
                        </Grid2>
                    </Box>
                </Popover>
            )}
        </>
    );
}

export default function ConfigurableDeviceInfo({ headerConfig, batteryInfo, batteryTags, getSOCColors, operationalStatus }) {
    const { t } = useTranslation(["common", "digital_twin"]);
    const vehicleInfoContext = useVehicleInfo();
    const vehicleInfo = useMemo(() => vehicleInfoContext ?? {}, [vehicleInfoContext]);
    const canViewPlatformAlerts = usePlatformAlertsAccess();
    const { batteryID } = useBatteryInfo();
    const [tooltipTitle, setTooltipTitle] = useState("");
    const [iotState, setIotState] = useState({});
    const needsIotState = headerNeedsIotState(headerConfig);

    // The IoT state (/api/iot/<id>/state, keys ext_<signalType>_<attributeName>) is fetched
    // only when a header part reads it, e.g. signal strength.
    useEffect(() => {
        if (!needsIotState || !batteryID) return undefined;
        let cancelled = false;
        getIoTState(batteryID)
            .then((res) => { if (!cancelled) setIotState(res?.response?.data ?? {}); })
            .catch((err) => console.error("Failed to fetch IoT state", err));
        return () => { cancelled = true; };
    }, [needsIotState, batteryID]);

    useEffect(() => {
        const title = batteryInfo.pingStatus?.toLowerCase() === "active"
            ? "Online"
            : "Offline since " + getMetricTimeStamp(batteryInfo.lastPingedTime, "relative").replace(/ ago$/, "");
        setTooltipTitle(title);
    }, [batteryInfo.pingStatus]);

    const { title = {}, subHeader = [], badges = [] } = headerConfig;
    const sources = useMemo(() => ({ batteryInfo, vehicleInfo, iotState }), [batteryInfo, vehicleInfo, iotState]);
    // Plain-string subHeader keeps its old fixed order and the dot before location.
    const { items: subHeaderItems, legacy: legacySubHeader } = subHeaderLayout(subHeader);
    const iconName = title.icon || "iot-device-icon";
    const { label: primaryLabel, value: primaryValue } = resolveTitle(title.primaryDetails, sources);
    const isActive = batteryInfo.pingStatus?.toLowerCase() === "active";
    const showStatusMarker = title.statusMarker !== false;

    const entityIcon = isActive ? (
        <Box sx={{ position: "relative" }}>
            <CustomIcon name={iconName} />
            <PrimaryTooltip title={tooltipTitle} placement="bottom">
                <Box sx={{ position: "absolute", right: "-11px", bottom: "-11px" }}>
                    <Lottie
                        options={{ loop: true, autoplay: true, animationData: GreenBlinkingLottie, rendererSettings: { preserveAspectRatio: "xMidYMid slice" } }}
                        height={26}
                        width={26}
                    />
                </Box>
            </PrimaryTooltip>
        </Box>
    ) : (
        <Badge
            overlap="circular"
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            badgeContent={
                <PrimaryTooltip title={tooltipTitle} placement="bottom">
                    <Box sx={{ display: "block", backgroundColor: "#8F8F8F", color: "#8F8F8F", width: "10px", height: "10px", borderRadius: "50%", position: "absolute", bottom: "2px", right: "2px", minWidth: "10px" }} />
                </PrimaryTooltip>
            }
        >
            <CustomIcon name={iconName} />
        </Badge>
    );

    const renderSubHeaderItem = (item, index) => {
        if (item.type === "signalStrength") {
            return (
                <Box key={`signal-${index}`} sx={{ marginRight: "4px" }}>
                    <CustomIcon name="network-strength-icon" strength={signalStrengthBars(item, sources)} />
                </Box>
            );
        }
        if (item.type === "pingStatus") {
            return (
                <Box key={`ping-${index}`} sx={{ display: "flex" }}>
                    <Typography variant="fw500s12lh24c939393">{t("digital_twin:last_pinged_status")}:&nbsp;</Typography>
                    <Typography variant="fw500s12lh24c494949">
                        {t(`common:battery_status.${normalizeKey(batteryInfo.batteryStatus)}`, batteryInfo.batteryStatus)}
                    </Typography>
                    <Typography variant="fw500s12lh24c494949">&nbsp;-&nbsp;</Typography>
                    {batteryInfo.lastPingedTime ? (
                        <PrimaryTooltip title={getMetricTimeStamp(batteryInfo.lastPingedTime)} placement="bottom">
                            <Typography variant="fw500s12lh24c444B51">{getMetricTimeStamp(batteryInfo.lastPingedTime, "relative")}</Typography>
                        </PrimaryTooltip>
                    ) : (
                        <Typography variant="fw500s12lh24c444B51">NA</Typography>
                    )}
                </Box>
            );
        }
        if (item.type === "location" && batteryInfo.location) {
            return (
                <React.Fragment key={`location-${index}`}>
                    {(legacySubHeader || index > 0) && <DotSeparator />}
                    <PrimaryTooltip title={batteryInfo.location} placement="bottom">
                        <Typography variant="fw500s12lh24c494949" sx={{ maxWidth: "200px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "inline-block" }}>
                            {batteryInfo.location}
                        </Typography>
                    </PrimaryTooltip>
                    {item.map && <BatteryDeviceInfoMapDialog vehicleInfo={vehicleInfo} batteryTags={batteryTags} />}
                </React.Fragment>
            );
        }
        return null;
    };

    return (
        <>
            <Box sx={{ display: "flex", marginBottom: "4px" }}>
                {/* Operational status marker on the free top-right corner of the entity
                    icon; the connectivity marker keeps bottom-right. */}
                <Box>
                    {showStatusMarker ? (
                        <FieldStatusIndicator
                            name="operationalStatus"
                            value={operationalStatus?.status}
                            reason={operationalStatus?.reason}
                            size={14}
                            gutter={0}
                            offset={{ x: -6, y: -2 }}
                        >
                            {entityIcon}
                        </FieldStatusIndicator>
                    ) : entityIcon}
                </Box>
                <Box sx={{ display: "flex", flexDirection: "column", paddingLeft: "12px", marginBottom: "4px" }}>
                    <Typography variant="fw400s12c959595" sx={{ lineHeight: "16px" }}>{primaryLabel}</Typography>
                    <Grid2 container gap={1} alignItems="center" mt={0.5}>
                        {title.primaryDetails?.copyable && primaryValue !== null && !headerConfig?.aboutPopup?.enabled ? (
                            <TextWithCopy text={String(primaryValue)} type={primaryLabel} textVariant="fw600s16lh24c444B51" />
                        ) : (
                            <AboutPopover config={headerConfig} batteryInfo={batteryInfo} sources={sources} primaryValue={primaryValue} />
                        )}
                        {title.primaryDetails?.editable && <EditVehicleDetailsDialog />}
                    </Grid2>
                </Box>
            </Box>

            <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", marginBottom: "20px" }}>
                {subHeaderItems.map(renderSubHeaderItem)}
            </Box>

            <Box sx={{ display: "flex", gap: "12px", alignItems: "center" }}>
                {canViewPlatformAlerts && badges.includes("activeAlerts") && batteryTags?.active_alerts?.value > 0 && (
                    <CustomTag
                        icon="alert-icon"
                        text={batteryTags.active_alerts.value}
                        containerStyle={{ padding: "2px 12px", borderRadius: "8px" }}
                        fontColor="#2E2E2E"
                        backgroundColor="#FFEEE8"
                        tooltipText="Active Smart Alerts"
                        fontSize="12px"
                        fontWeight={500}
                    />
                )}
                {badges.includes("bmsFaults") && batteryTags?.active_diagnostics?.value > 0 && (
                    <CustomTag
                        icon="alert-icon"
                        text={`Battery System Faults: ${batteryTags.active_diagnostics.value}`}
                        containerStyle={{ padding: "2px 12px", borderRadius: "8px" }}
                        fontColor="#2E2E2E"
                        backgroundColor="#FFF1F1"
                        fontSize="12px"
                        fontWeight={500}
                    />
                )}
                {badges.includes("soc") && (
                    <CustomTag
                        icon={false}
                        text={`SOC: ${formatSocValue(batteryTags?.soc?.value)} %`}
                        containerStyle={{ padding: "2px 12px", borderRadius: "8px" }}
                        fontColor={getSOCColors(batteryTags?.soc?.value).soc_text_color}
                        backgroundColor={getSOCColors(batteryTags?.soc?.value).soc_bg_color}
                        fontSize="12px"
                        fontWeight={500}
                    />
                )}
            </Box>
        </>
    );
}
